import PropTypes from 'prop-types';
import React from 'react';
import { Field, Fields, FieldArray } from 'redux-form/immutable';
import { Button, FormControl, Grid, InputLabel, MenuItem } from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import config from 'config';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormValueSelector from 'retired/FormValueSelector';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormTextField from 'retired/FormTextField';
import FormCheckbox from 'retired/FormCheckbox';
import * as validators from 'utils/validators';
import { currencies } from 'utils/currencyFormat';
import DialogButton from 'retired/DialogButton';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import FormSelect from 'retired/FormSelect';
import { Features } from 'utils/features';
import { TotalLimitLabel } from 'containers/DonationRulesListPage/TotalLimitLabel';
import FormContentSelector from 'retired/FormContentSelector';

const required = validators.required();

const CurrencyCodeSelector = ({ budget, ...rest }) => (
  <FormSelect displayEmpty {...rest}>
    {[{ currencyCode: '', currency: { label: 'Select a currency' } }]
      .concat(
        Object.keys(currencies).map((currencyCode) => ({
          currencyCode,
          currency: currencies[currencyCode],
        }))
      )
      .map(({ currencyCode, currency }) => (
        <MenuItem
          key={currencyCode}
          value={currencyCode}
          disabled={budget?.currencyCode !== currencyCode}
        >
          {currency.label}
        </MenuItem>
      ))}
  </FormSelect>
);
CurrencyCodeSelector.propTypes = {
  budget: PropTypes.object,
};

const DonationMatchingRule = ({
  donationMatchingRule,
  locations,
  budget,
  categories,
  campaigns,
  pristine,
  invalid,
  remove,
  donationMatchingRuleId,
  user,
}) => {
  const selectedCurrencyCode =
    donationMatchingRule?.get('currencyCode') || budget.currencyCode || 'USD';
  const selectedCurrency = currencies[selectedCurrencyCode];

  let spellOut = `Matching all donations${
    donationMatchingRule?.get('limitToCurrency') ? ` in ${selectedCurrency.code}` : ''
  } by ${donationMatchingRule?.get('matchingPercentage')}%`;
  if (donationMatchingRule?.get('limitPerUser')) {
    spellOut += ` with a limit of ${selectedCurrency.symbol}${donationMatchingRule?.get(
      'limitPerUser'
    )} per user`;
  }
  if (donationMatchingRule?.get('startingAt')) {
    spellOut += ` starting at ${format(
      legacyParse(donationMatchingRule?.get('startingAt')),
      convertTokens('YYYY-MM-DD')
    )}`;
  }
  if (donationMatchingRule?.get('endingAt')) {
    if (donationMatchingRule?.get('startingAt')) {
      spellOut += ' and';
    }
    spellOut += ` ending at ${format(
      legacyParse(donationMatchingRule?.get('endingAt')),
      convertTokens('YYYY-MM-DD')
    )}`;
  }

  const categoriesString = donationMatchingRule
    ?.get('categories')
    ?.map((id) => `'${categories?.get(id)?.get('name')}'`)
    ?.join(', ');
  if (donationMatchingRule?.get('categoriesExclude')) {
    if (donationMatchingRule?.get('categories').size) {
      spellOut += ` and excluding fundraisers and nonprofits in the categories ${categoriesString}`;
    }
  } else if (donationMatchingRule?.get('categories').size) {
    spellOut += ` and including fundraisers and nonprofits in the categories ${categoriesString}`;
  }

  const campaignsString = donationMatchingRule
    ?.get('campaigns')
    ?.map((id) => `'${campaigns?.get(id)?.get('name')}'`)
    ?.join(', ');
  if (donationMatchingRule?.get('campaignsExclude')) {
    if (donationMatchingRule?.get('campaigns').size) {
      spellOut += ` and excluding fundraisers and nonprofits in the campaigns ${campaignsString}`;
    }
  } else if (donationMatchingRule?.get('campaigns')?.size) {
    spellOut += ` and including fundraisers and nonprofits in the campaigns ${campaignsString}`;
  }

  if (donationMatchingRule?.get('nonprofitsExclude')) {
    if (donationMatchingRule?.get('nonprofits').size) {
      spellOut += ' and excluding nonprofits selected above';
    }
  } else if (donationMatchingRule?.get('nonprofits').size) {
    spellOut += ' and including nonprofits selected above';
  }

  if (donationMatchingRule?.get('fundraisersExclude')) {
    if (donationMatchingRule?.get('fundraisers').size) {
      spellOut += ' and excluding fundraisers selected above';
    }
  } else if (donationMatchingRule?.get('fundraisers').size) {
    spellOut += ' and including fundraisers selected above';
  }

  if (donationMatchingRule?.get('locationsExclude')) {
    if (donationMatchingRule?.get('locations').size) {
      spellOut += ' and excluding employee locations selected above';
    }
  } else if (donationMatchingRule?.get('locations').size) {
    spellOut += ' and only including employee locations selected above';
  }

  if (donationMatchingRule?.get('maxDistance')) {
    spellOut += ` and nonprofit is located within ${donationMatchingRule?.get(
      'maxDistance'
    )} miles of employee (work location)`;
  }

  const validateCategories = (value, rule) => {
    if (rule && !rule.get('categoriesExclude') && !value?.size) {
      return 'The rule is set to only include fundraisers and nonprofits with certain categories but none are selected';
    }
    return undefined;
  };

  const validateCampaigns = (value, rule) => {
    if (rule && !rule.get('campaignsExclude') && !value?.size) {
      return 'The rule is set to only include fundraisers and nonprofits with certain campaigns but none are selected';
    }
    return undefined;
  };

  const validateNonprofits = (value, rule) => {
    if (
      rule &&
      !rule.get('nonprofitsExclude') &&
      !rule.get('partneredNonprofits') &&
      !value?.size
    ) {
      return 'The rule is set to only include certain nonprofits and none are selected';
    }
    return undefined;
  };

  const validateFundraisers = (value, rule) => {
    if (rule && !rule.get('fundraisersExclude') && !value?.size) {
      return 'The rule is set to only include certain fundraisers and none are selected';
    }
    return undefined;
  };

  const validateLocations = (value, rule) => {
    if (rule && !rule.get('locationsExclude') && !value?.size) {
      return 'The rule is set to only include certain locations and none are selected';
    }
    return undefined;
  };

  const validateMatchingPercentage = (value) => {
    if (!value) {
      return undefined;
    }
    const valueAsNumber = Number(value);
    if (valueAsNumber < 0) {
      return 'Matching percentage cannot be negative';
    }

    return undefined;
  };

  const validateLimitPerUser = (value) => {
    if (!value) {
      return undefined;
    }
    const valueAsNumber = Number(value);
    if (valueAsNumber < 0) {
      return 'Limit per user cannot be negative';
    }

    return undefined;
  };

  const validateTotalLimit = (value, rule) => {
    if (!value) {
      return undefined;
    }
    const valueAsNumber = Number(value);
    if (valueAsNumber === 0) {
      return 'Total limit cannot equal 0';
    }
    if (rule && Number(rule.get('limitPerUser')) > valueAsNumber) {
      return 'Total limit can not be less than limit per user';
    }
    if (rule && Number(rule.get('totalIssued')) > valueAsNumber) {
      return 'You can not set the limit to less than what has already been spent';
    }
    return undefined;
  };

  const validatePriority = (value) => {
    if (!value) {
      return undefined;
    }
    const valueAsNumber = Number(value);
    if (valueAsNumber < -100 || valueAsNumber > 100) {
      return 'Rule priority must be within -100 and 100';
    }

    return undefined;
  };

  return (
    <div
      style={{
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 30,
        marginBottom: 30,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ flexGrow: 1 }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Rule Name</InputLabel>
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <Field
              name="title"
              component={FormTextField}
              placeholder="Rule Name"
              validate={required}
              required
            />
          </FormControl>
        </div>
        {donationMatchingRuleId !== 'new' &&
          !donationMatchingRule?.get('donationsMatchedCount') && (
            <DialogButton
              style={{ display: 'inline-block' }}
              title="Remove Rule"
              text="Are you sure you want to remove this Donation Matching Rule?"
              confirmLabel="Remove"
              cancelLabel="Cancel"
              iconButton
              handleConfirm={remove}
            >
              <DeleteIcon />
            </DialogButton>
          )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <Grid container spacing={3}>
          <Grid item md={4}>
            <Field
              name="currencyCode"
              component={CurrencyCodeSelector}
              validate={required}
              required
              type="string"
              budget={budget}
              defaultValue={selectedCurrencyCode}
              label="Rule Currency"
              labelProps={{
                shrink: true,
              }}
              formControlProps={{
                style: { width: '60%', height: 17 },
              }}
            />
          </Grid>
          <Grid item md={4}>
            <div style={{ marginTop: 17 }}>
              <Field
                name="limitToCurrency"
                component={FormCheckbox}
                label="Limit to donations in budget currency"
              />
            </div>
          </Grid>
          {user?.hasFeature(Features.DONATION_CREDIT) ? (
            <Grid item md={4}>
              <div style={{ marginTop: 17 }}>
                <Field
                  name="excludeDonationCreditDonations"
                  component={FormCheckbox}
                  label="Exclude donations made with donation credit"
                />
              </div>
            </Grid>
          ) : null}
        </Grid>
      </div>
      <div style={{ marginBottom: 30 }}>
        <Grid container spacing={3}>
          <Grid item md>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Matching Percentage</InputLabel>
            </FormControl>
            <Field
              name="matchingPercentage"
              component={FormTextField}
              placeholder="Matching percentage, e.g. 100%"
              validate={[required, validateMatchingPercentage]}
              alwaysDisplayErrors
              required
              type="number"
            />
            %
          </Grid>
          <Grid item md>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Limit Per User</InputLabel>
            </FormControl>
            {selectedCurrency.symbol}
            <Field
              name="limitPerUser"
              component={FormTextField}
              placeholder="Limit Per User"
              validate={validateLimitPerUser}
              type="number"
            />
          </Grid>
          <Grid item md>
            <TotalLimitLabel />
            {selectedCurrency.symbol}
            <Field
              name="limitTotal"
              component={FormTextField}
              placeholder="Total Limit"
              validate={validateTotalLimit}
              alwaysDisplayErrors
              type="number"
            />
          </Grid>
        </Grid>
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>
            Choose start and end dates when this rule should be active or leave empty
          </InputLabel>
        </FormControl>

        <div>
          <Fields
            names={[`startingAt`, `endingAt`]}
            component={FormDateWithStartAndEnd}
            displayTimeAndTimeZone
            future
          />
        </div>
      </div>

      <div style={{ margin: '20px 0 20px 0' }}>
        <Field
          name="bypassBudgetLimitPerUser"
          component={FormCheckbox}
          label="Do not apply individual budget cap to these funds"
        />
        <div>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Rule priority</InputLabel>
          </FormControl>
          <Field
            name="priority"
            component={FormTextField}
            placeholder="0"
            validate={validatePriority}
            inputProps={{
              min: '-100',
              max: '100',
              step: 1,
            }}
            alwaysDisplayErrors
            type="number"
            helperText="Higher numbers takes priority"
          />
        </div>
        {(config.isDemo || config.isDevelopment) && (
          <div>
            <Field name="appliesToGuests" component={FormCheckbox} label="Apply to guest donors" />
          </div>
        )}
      </div>

      {categories.size > 0 ? (
        <div style={{ marginBottom: 30 }}>
          <FormIncludeExcludeSelector
            name="categoriesExclude"
            labelText="the following categories:"
          />
          <FieldArray
            name="categories"
            component={FormValueSelector}
            selectMultiple
            options={categories}
            validate={validateCategories}
          />
        </div>
      ) : null}

      {campaigns?.size > 0 ? (
        <div style={{ marginBottom: 30 }}>
          <FormIncludeExcludeSelector
            name="campaignsExclude"
            labelText="the following campaigns:"
          />
          <FieldArray
            name="campaigns"
            component={FormValueSelector}
            selectMultiple
            // @NOTE-CH: We don't want to let them select Universal Campaigns for now as it's not supported by the `app` yet
            options={campaigns.filter((campaign) => campaign.get('autoTagDonations') === false)}
            validate={validateCampaigns}
          />
        </div>
      ) : null}

      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector
          name="nonprofitsExclude"
          labelText="the following nonprofits:"
        />
        <div style={{ marginTop: 10 }}>
          <FieldArray
            name="nonprofits"
            component={FormNonprofitsSelector}
            validate={validateNonprofits}
          />
        </div>
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector
          name="fundraisersExclude"
          labelText="the following fundraisers:"
        />
        <div style={{ marginTop: 10 }}>
          <FieldArray
            name="fundraisers"
            component={FormContentSelector}
            deedTypes={['Campaign']}
            variant="standard"
            validate={validateFundraisers}
            sortable={false}
          />
        </div>
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector name="locationsExclude" labelText="the following locations:" />
        <Field
          name="locations"
          component={FormLocationSelector}
          props={{ locations }}
          validate={validateLocations}
        />
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>Limit Matches to Donors Near the Nonprofit</InputLabel>
        </FormControl>
        <Field
          name="maxDistance"
          component={FormTextField}
          placeholder="Maximum distance in miles"
          alwaysDisplayErrors
          type="number"
        />
      </div>
      <div>Rule summary:&#20;{spellOut}</div>

      <div style={{ marginTop: 30 }}>
        <Button type="submit" disabled={invalid} color="primary" variant="contained">
          {pristine ? 'Close' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};
DonationMatchingRule.propTypes = {
  donationMatchingRule: PropTypes.object,
  categories: PropTypes.object,
  campaigns: PropTypes.object,
  budget: PropTypes.object,
  locations: PropTypes.object,
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
  remove: PropTypes.func,
  donationMatchingRuleId: PropTypes.string,
  user: PropTypes.object,
};

export default DonationMatchingRule;
